import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { AuthMessageService } from './auth.service';
import { ReferencepinloginService } from '../app/serial-pinlogin/referencepinlogin.service';
import { SessionService } from './session.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private currentUrl: string = '';

  private readonly EXCLUDED_URLS = [
    '/api/auth/reference-pin-login',
    '/api/auth/authenticate',
    '/api/payment-applicant/login',
    '/api/auth/signup',
    '/api/auth/register',
    '/api/auth/forgot-password',
    '/api/auth/reset-password',
  ];

  constructor(
    private router: Router,
    private authMessageService: AuthMessageService,
    private authService: ReferencepinloginService,
    private sessionService: SessionService
  ) {
    // Track current URL to handle navigation-in-progress scenarios
    this.currentUrl = this.router.url;
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
        console.log('🔄 Router URL updated to:', this.currentUrl);
      }
    });
  }


  private getTokenFromAllSources(): string | null {
    // Check all possible storage locations
    const sources = [
      () => sessionStorage.getItem('authToken'),    
      () => this.authService.getToken(),              
      () => localStorage.getItem('authToken'),        
      () => localStorage.getItem('token'),           
    ];

    for (const getToken of sources) {
      const token = getToken();
      if (token) {
        return token;
      }
    }

    return null;
  }

  /**
   * For debugging - identifies which storage location provided the token
   */
  private getTokenSource(): string {
    if (sessionStorage.getItem('authToken')) return 'sessionStorage.authToken';
    if (localStorage.getItem('authToken')) return 'localStorage.authToken';
    if (localStorage.getItem('token')) return 'localStorage.token';
    return 'none';
  }

  private isExcludedUrl(url: string): boolean {
    return this.EXCLUDED_URLS.some((excludedUrl) => url.includes(excludedUrl));
  }

  /**
   * CRITICAL FIX: Detects if we're in the payment flow (email/password login path)
   */
  private isInPaymentFlow(): boolean {
    const urlsToCheck = [
      this.currentUrl,
      this.router.url,
      window.location.hash.replace('#', '')
    ];

    const paymentPaths = [
      '/auth-payment',
      '/payment-success',
      '/payment-cancelled',
      '/verify-otp',
      '/signup',
      '/home'
    ];

    const isInFlow = urlsToCheck.some(url => 
      paymentPaths.some(path => url?.includes(path))
    );

    return isInFlow;
  }

  /**
   * NEW: Detects if we're in the application flow (reference-pin login path)
   */
  private isInApplicationFlow(): boolean {
    const urlsToCheck = [
      this.currentUrl,
      this.router.url,
      window.location.hash.replace('#', '')
    ];

    const applicationPaths = [
      '/main-application-dashboard',
      '/main-applcation-login', 
      '/portfolio-review',
      '/previous-university',
      '/scholarship',
      '/sponsors-particulars',
      '/congratulations'
    ];

    const isInFlow = urlsToCheck.some(url => 
      applicationPaths.some(path => url?.includes(path))
    );

    return isInFlow;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authService.getRefreshToken();
      if (!refreshToken) {
        // No refresh token, logout
        this.logoutUser('Session expired. Please log in again.');
        return throwError(() => new Error('No refresh token available'));
      }

      return this.authService.refreshToken(refreshToken).pipe(
        switchMap((resp: any) => {
          this.isRefreshing = false;

          // Store new tokens
          this.authService.storeToken(resp.accessToken);
          if (resp.refreshToken) {
            this.authService.storeRefreshToken(resp.refreshToken);
          }
          this.refreshTokenSubject.next(resp.accessToken);

          // Retry failed request with new token
          return next.handle(this.addToken(request, resp.accessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.logoutUser('Session expired. Please log in again.');
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token!)))
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private logoutUser(message: string) {
    console.log('🚪 Logging out user:', message);
    this.authMessageService.setErrorMessage(message);
    this.authService.logout();
    
    const isPaymentFlow = this.isInPaymentFlow();
    const isApplicationFlow = this.isInApplicationFlow();
    
    if (isApplicationFlow) {
      this.clearApplicationTokens();
      this.sessionService.triggerSessionExpired(); 
    } else if (!isPaymentFlow) {
      this.router.navigate(['/home']);
    } else {
      console.log('⏸️ Skipping redirect - in payment flow');
    }
  }


  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  const authToken = this.getTokenFromAllSources();
  
  let authReq = request;
  if (authToken) {
    authReq = request.clone({
      setHeaders: { Authorization: `Bearer ${authToken}` },
    });
  }

  return next.handle(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const isExcludedUrl = this.isExcludedUrl(request.url);
        if (isExcludedUrl) {
          return throwError(() => error);
        }

        const isApplicationFlow = this.isInApplicationFlow();
        const isPaymentFlow = this.isInPaymentFlow();

        if (isApplicationFlow) {
          // ← Token expired in application flow → clear and redirect to their login
            console.log('🔄 Token expired in application flow - showing session popup');
          this.clearApplicationTokens();
            this.sessionService.triggerSessionExpired(); // ← REPLACES router.navigate
            return throwError(() => error);
        }

        if (isPaymentFlow) {
          // ← Token expired in payment flow → redirect to home
          console.log('🔄 Token expired in payment flow - redirecting to home');
          this.clearApplicationTokens();
          this.router.navigate(['/home']);
          return throwError(() => error);
        }

        return this.handle401Error(authReq, next);
      }
      return throwError(() => error);
    })
  );
}


private clearApplicationTokens(): void {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('_act_ls');
  sessionStorage.removeItem('access_token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('token');
  localStorage.removeItem('_act_ls');
  localStorage.removeItem('access_token');
}



}

